/**
 * asset-roots.ts — the single definition of *where* skaile-owned content lives.
 *
 * The policy sits in `core/` rather than next to either of its callers because
 * both layers that need it already depend on `core/`: the CLI's discovery
 * surfaces (`flow list`, completions, skill walking) and the flow connector's
 * resolution surfaces under `factory-assets/` (`resume`, sub-flow targets,
 * `serve`). Keeping it in `cli/` instead would force a `factory-assets → cli`
 * import, which is a real module cycle — and duplicating it there is exactly
 * how the two lists drifted apart in the first place.
 */

import { existsSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, join } from "node:path";
import { SUPPORTED_DRIVER_TARGETS } from "./driver-targets.js";
import { factoryAssetRoots } from "./runtime-assets.js";
import { deployedBase } from "./store.js";

/**
 * Root of the per-user skaile data directory (`~/.skaile`).
 *
 * Deliberately **not** routed through `globalSkaileDir()`: that one honours
 * `$SKAILE_HOME`, this one is the fixed home-relative path the library, index
 * and config layers have always written to. Reconciling the two would move
 * users' data, so it is a behaviour change rather than a cleanup.
 *
 * @docLink packages/core/concepts#discovery
 */
export function skaileHomeDir(): string {
  return join(homedir(), ".skaile");
}

/**
 * The library checkouts directory before any environment override — the value
 * `getConfigDefaults()` reports as the *default*, which must stay independent of
 * `SKAILE_LIBRARIES_DIR` so a config file can still be seen to override it.
 * Callers that want the effective directory want {@link resolveLibraryDir}.
 *
 * @docLink packages/core/concepts#discovery
 */
export function defaultLibraryDir(): string {
  return join(skaileHomeDir(), "libraries");
}

/**
 * Resolve the active library checkouts directory.
 *
 * `SKAILE_LIBRARIES_DIR` overrides the default `~/.skaile/libraries`.
 *
 * @docLink packages/core/concepts#discovery
 */
export function resolveLibraryDir(): string {
  if (process.env.SKAILE_LIBRARIES_DIR) return process.env.SKAILE_LIBRARIES_DIR;
  return defaultLibraryDir();
}

/**
 * The skaile-owned install roots for one scope — the parent of the directory
 * `skaile install` / `skaile add` deploy flows into.
 *
 * Derived through `deployedBase`, the same function the installer calls, so
 * discovery cannot drift from where the bytes land. Every driver target maps
 * `flow` to the same `.skaile/flows`, so the three results collapse in the
 * caller's `Set`.
 */
function installRoots(globalInstall: boolean, projectDir: string): string[] {
  return SUPPORTED_DRIVER_TARGETS.map((target) =>
    dirname(deployedBase("flow", target, globalInstall, projectDir)),
  );
}

/**
 * All content roots that skill / flow discovery should walk, in priority order,
 * filtered to those that exist on disk and de-duplicated.
 *
 * Four layers, in this order:
 * 1. The project-local install root `<projectDir>/.skaile/`, where
 *    `skaile install` / `skaile add` deploy flows.
 * 2. The first-party `factory-assets/` roots from the shared `factoryAssetRoots()`
 *    helper — the `SKAILE_FACTORY_ASSETS_DIR` override layer (when set) ahead of
 *    the bundled tree (in-repo source in dev, `dist/factory-assets/` after
 *    install). No monorepo special-case.
 * 3. The user-library dir `~/.skaile/libraries/`.
 * 4. The user-global install root `~/.skaile/`.
 *
 * Order is significant: `findSkills` / flow discovery dedupe first-root-wins, so
 * the env override shadows the bundled tree on a name clash. The project-local
 * install root leads because `deployAll` applies `patches:` to the deployed copy
 * — that copy, not the factory original, is the project's effective flow.
 *
 * Only `flow` installs into skaile's own `.skaile/` tree; every other asset kind
 * installs into the driver's directory (`.claude/`, `.omp/`, `.codex/`), which
 * the driver reads natively and skaile deliberately does not enumerate. So the
 * roots below are exactly the skaile-owned ones — an invariant asserted by
 * `cli/tests/installed-asset-roots.test.ts`.
 *
 * This is the discovery surface used by `findSkills`, flow discovery, and the
 * completion resolvers; flow *resolution* walks these same roots behind
 * `flowLookupRoots`, which only prepends the project's own `ai-assets/` tree.
 *
 * @param projectDir - Project directory whose local install root to include
 *   (default: `process.cwd()`).
 * @docLink packages/core/concepts#discovery
 */
export function aiResourceRoots(projectDir: string = process.cwd()): string[] {
  const roots: string[] = [
    ...installRoots(false, projectDir),
    ...factoryAssetRoots(),
    // resolveLibraryDir honors SKAILE_LIBRARIES_DIR (the authoring side does too),
    // so a custom-dir library is visible to discovery instead of being missed.
    resolveLibraryDir(),
    ...installRoots(true, projectDir),
  ];
  return [...new Set(roots)].filter((r) => existsSync(r));
}
